"use client";

import { motion } from "framer-motion";
import { useIsMobile } from "@/hooks/use-mobile";

interface AnimatedErrorAlertProps {
  variant?: "default" | "success" | "error";
}

export const AnimatedErrorAlert = ({
  variant = "error",
}: AnimatedErrorAlertProps) => {
  const isMobile = useIsMobile();

  // Use red circle with white X on mobile
  const circleColor = isMobile
    ? "rgb(220 38 38)" // red-600
    : variant === "error"
      ? "white"
      : "rgb(220 38 38)";
  const xColor = isMobile
    ? "white"
    : variant === "error"
      ? "rgb(220 38 38)"
      : "white";

  const baseSize = 28;
  const size = isMobile ? 32 : 45;

  // Increase padding to make X smaller (changed from 0.3 to 0.35)
  const padding = baseSize * 0.35;
  const x1 = `M ${padding} ${padding} L ${baseSize - padding} ${
    baseSize - padding
  }`;
  const x2 = `M ${baseSize - padding} ${padding} L ${padding} ${
    baseSize - padding
  }`;

  return (
    <motion.svg
      width={size}
      height={size}
      viewBox={`0 0 ${baseSize} ${baseSize}`}
      initial='hidden'
      animate='visible'
    >
      <motion.circle
        cx={baseSize / 2}
        cy={baseSize / 2}
        r={baseSize / 2 - 4}
        fill={circleColor}
        variants={{
          hidden: {
            scale: 0,
            opacity: 0,
          },
          visible: {
            scale: 1,
            opacity: 1,
            transition: {
              duration: 0.3,
              ease: [0.34, 1.56, 0.64, 1],
            },
          },
        }}
      />
      <motion.path
        fill='none'
        stroke={xColor}
        strokeWidth={3}
        strokeLinecap='round'
        strokeLinejoin='round'
        d={x1}
        variants={{
          hidden: {
            pathLength: 0,
            opacity: 0,
          },
          visible: {
            pathLength: 1,
            opacity: 1,
            transition: {
              delay: 0.1,
              duration: 0.2,
              ease: "easeOut",
              opacity: { duration: 0.1 },
            },
          },
        }}
      />
      <motion.path
        fill='none'
        stroke={xColor}
        strokeWidth={3}
        strokeLinecap='round'
        strokeLinejoin='round'
        d={x2}
        variants={{
          hidden: {
            pathLength: 0,
            opacity: 0,
          },
          visible: {
            pathLength: 1,
            opacity: 1,
            transition: {
              delay: 0.2,
              duration: 0.2,
              ease: "easeOut",
              opacity: { duration: 0.1 },
            },
          },
        }}
      />
    </motion.svg>
  );
};
